// ---------------------------------------------------------
// Datei: DocumentChecklist.tsx
// ---------------------------------------------------------
import React, { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useDocumentStore } from "../store/documentState";
import { useDealsStore } from "../store/dealsStore";
import { DocumentType, UploadedDocumentFile } from "../types/documents";
import { getSignedUrlAsync as getSignedUrl, updateTextPropertyAsync as updateDealTextProperty } from "../services/api/hubspot";
import { deleteFileAsync as deleteDriveFile } from "../services/api/drive";
import { getFileAsync as getDriveFile } from "../services/api/drive";
import { uploadMarkedPhotoAsync, getDriveFileMetadataAsync } from "../services/api/drive";
import FormularModal from "./FormularModal";
import DocumentPreviewModal from "./DocumentPreviewModal";
import { DealContact } from "../types/hubspot";
import { useQueue } from "../contexts/QueueContext";
import { peekQueueBlob } from "../services/queueService";
import { useAuth } from "../contexts/AuthContext";
import {
  getDriveFileIdFromUrl,
  uploadedDocumentFilesToPropertyValue,
} from "../utils/uploadedDocumentFiles";
import type { UploadFile } from "./FileUploadModal";
import { DOCUMENT_TO_PROPERTY_MAP } from "../services/hubspotDealMapper";
import { isMarkedFileName, withMarkedFileNameSuffix } from "../hooks/usePhotoAnnotationGallery";
import PhotoAnnotationEditor from "./PhotoAnnotationEditor";
import CaptionIndicator from "./CaptionIndicator";
import { resolvePhaseFolderName } from "../utils/phaseFolder";

function formatConsentTimestamp(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    + ' Uhr';
}

interface UploadedFilePair {
  original: UploadedDocumentFile;
  marked?: UploadedDocumentFile;
}

const uploadedFileKey = (file: UploadedDocumentFile): string | undefined =>
  file.fileId || file.url || file.id;

// Eine Datei ist selbst eine Markierung, wenn sie explizit über `originalId` auf ihr Original
// zurückverweist (neu, robust) ODER — für vor dieser Änderung bereits gespeicherte Markierungen,
// die noch keine `originalId` haben — ihr Name der "-markiert"-Konvention folgt (Altfall-Fallback).
const isMarkedUploadedFile = (file: UploadedDocumentFile): boolean =>
  Boolean(file.originalId) || Boolean(file.name && isMarkedFileName(file.name));

// Ticket #1055 (Nutzer-Rückmeldung): Original und markierte Version landen hier als zwei getrennte
// Einträge in der flachen Dateiliste eines Multi-Upload-Dokuments (siehe handleSaveGalleryEdit) —
// ohne Pairing würden beide unmarkiert als zwei eigenständige Bilder gezählt/angezeigt. Analog zu
// pairFotosWithMarkedVersions in ProjektvorbereitungBriefing.tsx, aber ohne Ordner-Gruppierung
// (diese Liste ist bereits pro Dokument flach).
// Matching primär über `originalId` (robust, unabhängig vom Dateinamen) statt reiner Namens-
// konvention, weil Originale hier oft KEINEN Namen haben (z.B. eine reine HubSpot-URL-Referenz
// aus `inneneinheit_rohranschluss` ohne `name`-Feld — Namensvergleich würde dort nie matchen und
// Original+Markierung blieben zwei getrennte Kacheln, s. Nutzer-Rückmeldung/Screenshot).
const pairUploadedFilesWithMarkedVersions = (files: UploadedDocumentFile[]): UploadedFilePair[] => {
  const byName = new Map(files.filter((f) => f.name).map((f) => [f.name as string, f] as const));
  const consumedMarkedKeys = new Set<string>();
  const pairs: UploadedFilePair[] = [];

  const findMarkedFor = (original: UploadedDocumentFile): UploadedDocumentFile | undefined => {
    const originalKey = uploadedFileKey(original);
    const byOriginalId = originalKey ? files.find((f) => f.originalId === originalKey) : undefined;
    if (byOriginalId) return byOriginalId;
    if (original.name) {
      return byName.get(withMarkedFileNameSuffix(original.name));
    }
    return undefined;
  };

  for (const file of files) {
    if (isMarkedUploadedFile(file)) {
      continue;
    }
    const marked = findMarkedFor(file);
    if (marked) {
      const markedKey = uploadedFileKey(marked);
      if (markedKey) consumedMarkedKeys.add(markedKey);
    }
    pairs.push({ original: file, marked });
  }

  // Verwaiste Markierungen (Original zwischenzeitlich gelöscht) trotzdem als eigene Kachel zeigen
  // statt sie kommentarlos zu verschlucken.
  for (const file of files) {
    const key = uploadedFileKey(file);
    if (isMarkedUploadedFile(file) && !(key && consumedMarkedKeys.has(key))) {
      pairs.push({ original: file });
    }
  }

  return pairs;
};

const toDisplayFile = (pair: UploadedFilePair): UploadedDocumentFile => pair.marked ?? pair.original;

interface DocumentChecklistProps {
  phaseId: string;
  // Ticket #1055: nur für die Foto-Markierung im Galerie-Viewer benötigt (uploadMarkedPhotoAsync
  // braucht die projectId für den Ziel-Drive-Ordner) — optional, damit bestehende Aufrufer ohne
  // projectId (falls es welche gibt) nicht brechen; ohne projectId bleibt "Bearbeiten" ausgeblendet.
  projectId?: string;
  onSelectDocument?: (document: DocumentType) => void;
  onDirectPhotoUpload?: (document: DocumentType, files: UploadFile[]) => void | Promise<void>;
  onError?: (error: string) => void;
  disabled?: boolean;
  dealContact?: DealContact;
  // Ticket #1126: erlaubt es, einzelne Dokumente aus dieser generischen Liste auszublenden bzw.
  // eine zweite Instanz auf genau ein Dokument zu beschränken, damit es an anderer Stelle
  // (z.B. neben AllgemeineUploads/Baustellendokumentation) platziert werden kann. Betrifft nur
  // das Rendering — Progress/Gate laufen weiterhin über den Store, unabhängig davon, wo ein
  // Dokument angezeigt wird.
  excludeDocumentIds?: string[];
  onlyDocumentIds?: string[];
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  phaseId,
  projectId,
  onSelectDocument,
  onDirectPhotoUpload,
  onError,
  disabled = false,
  dealContact,
  excludeDocumentIds,
  onlyDocumentIds,
}) => {
  const {
    fetchDocuments,
    isPhaseLoading,
    getPhaseError,
    updateDocumentStatus,
    deleteDocument,
    getDocumentData,
    getDocumentFileId,
    getDocumentFiles,
    saveDocumentFileId,
    saveDocumentData, // WICHTIG: Um die URL lokal zu speichern
    saveDocumentFiles,
  } = useDocumentStore();

  // WICHTIG: useShallow für reaktives Update bei nested Array-Änderungen
  // Zustand v5: useShallow vergleicht Array-Inhalte statt nur Referenzen
  const documents = useDocumentStore(
    useShallow((state) => state.documents[phaseId] || [])
  ) as DocumentType[];
  const visibleDocuments = documents.filter((doc) => {
    if (onlyDocumentIds && !onlyDocumentIds.includes(doc._id)) return false;
    if (excludeDocumentIds?.includes(doc._id)) return false;
    return true;
  });
  const loading = isPhaseLoading(phaseId);
  const error = getPhaseError(phaseId);
  const { items: queueItems, removeFromQueue } = useQueue();

  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [formularModalOpen, setFormularModalOpen] = useState(false);
  const [selectedFormular, setSelectedFormular] = useState<DocumentType | null>(null);
  
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<DocumentType | null>(null);
  const [previewData, setPreviewData] = useState<string>("");
  const [serialPreviewText, setSerialPreviewText] = useState<string | null>(null);
  const [serialPreviewTitle, setSerialPreviewTitle] = useState<string>('');
  const [galleryDocument, setGalleryDocument] = useState<DocumentType | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<UploadedDocumentFile[]>([]);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryPreviewData, setGalleryPreviewData] = useState<string>("");
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [thumbnailCache, setThumbnailCache] = useState<Record<string, string>>({});

  // Ticket #1055: Foto markieren im Galerie-Viewer, nur für Ops-Manager (identischer Backend-
  // Check wie Projektvorbereitung/VOT). Anders als dort gibt es hier keine Original/Markiert-
  // Paar-Anzeige (dieser generische Viewer listet Dateien als flache Reihe von Einzelbildern,
  // nicht nach Ordner gruppiert) — die markierte Version erscheint stattdessen als eigenes,
  // zusätzliches Bild in derselben Liste; ein erneutes Bearbeiten desselben Bildes ersetzt die
  // bereits vorhandene markierte Version in place (per Dateiname), statt eine weitere anzulegen.
  const { user } = useAuth();
  const effectiveIsOpsManager = Boolean(user?.isOpsManager);
  const [editingGalleryFile, setEditingGalleryFile] = useState<UploadedDocumentFile | null>(null);
  const [editingImageDataUrl, setEditingImageDataUrl] = useState<string | null>(null);
  // Nur gesetzt, wenn editingGalleryFile selbst eine Markierung ist — das unangetastete Original,
  // zu dem "Löschen" im Editor zurückspringt (wie in Projektvorbereitung/VOT).
  const [editingGalleryOriginal, setEditingGalleryOriginal] = useState<UploadedDocumentFile | null>(null);
  const [editingOriginalImageDataUrl, setEditingOriginalImageDataUrl] = useState<string | undefined>(undefined);
  const [editingLoading, setEditingLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const isFetchingRef = useRef(false);

  // === Load Documents ===
  useEffect(() => {
    if (!phaseId) { if (onError) onError("Phasen-ID fehlt"); return; }
    if ((documents && documents.length > 0) || loading || isFetchingRef.current) return;
    const isHubSpotPhase = phaseId.includes("-");
    if (isHubSpotPhase) return;
    isFetchingRef.current = true;
    fetchDocuments(phaseId).catch((err) => onError && onError(err.message)).finally(() => { isFetchingRef.current = false; });
  }, [phaseId, loading, documents, fetchDocuments]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // === NEUER HELPER: URL FETCHEN & ERSETZEN ===
  const fetchAndCacheHubSpotUrl = async (fileId: string, docId: string) => {
    try {
      console.log(`[HELPER] Hole URL für FileID ${fileId}...`);
      
      // 1. GET Call an Backend (wie dein Postman Call)
      const signedUrl = await getSignedUrl(fileId);
      
      if (signedUrl) {
        console.log(`[HELPER] Neue URL erhalten. Aktualisiere Anzeige.`);
        
        // 2. URL im lokalen Store speichern (überschreibt alte Daten/Bilder)
        saveDocumentData(phaseId, docId, signedUrl);
        return signedUrl;
      }
    } catch (e) {
      console.error("[HELPER] Fehler beim Abrufen der URL:", e);
    }
    return null;
  };

  // === Handlers ===

  const openDrivePreview = async (driveUrl: string, document: DocumentType) => {
    const driveFileId = driveUrl.split('/d/')[1]?.split('/')[0];
    if (!driveFileId) return;
    try {
      const d = await getDriveFile(driveFileId);
      if (d?.base64Content) {
        setPreviewDocument(document);
        setPreviewData(`data:${d.mimeType};base64,${d.base64Content}`);
        setPreviewModalOpen(true);
      }
    } catch (e) {
      console.error('[Preview] Drive fetch fehlgeschlagen', e);
    }
  };

  const isDurchfuehrungMultiUploadDocument = (document: DocumentType) =>
    phaseId.endsWith('-durchfuehrung') &&
    document._id !== 'seriennummer-ausseneinheit' &&
    document._id !== 'typenschild-altanlage-durchfuehrung';

  const isImageFile = (file: File) =>
    file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildUploadFiles = async (files: File[]): Promise<UploadFile[]> =>
    Promise.all(
      files.map(async (file) => ({
        id: `direct-photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: file.name,
        type: file.type || 'image/jpeg',
        size: file.size,
        dataUrl: await readFileAsDataUrl(file),
        file,
      }))
    );

  const getDirectPhotoInputId = (document: DocumentType) =>
    `direct-photo-input-${phaseId}-${document._id}`.replace(/[^a-zA-Z0-9_-]/g, '-');

  const handleDirectPhotoInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    document: DocumentType
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    if (selectedFiles.length === 0) return;
    setActionMenuOpen(null);

    const imageFiles = selectedFiles.filter(isImageFile);
    if (imageFiles.length === 0) {
      onError?.('Bitte nur Bilder auswählen.');
      return;
    }
    if (imageFiles.length !== selectedFiles.length) {
      onError?.('Einige Dateien wurden übersprungen, weil sie keine Bilder sind.');
    }

    try {
      closeDocumentGallery();
      const uploadFiles = await buildUploadFiles(imageFiles);
      await onDirectPhotoUpload?.(document, uploadFiles);
    } catch (uploadError) {
      console.error('[DocumentChecklist] Direkter Foto-Upload fehlgeschlagen', uploadError);
      onError?.('Bilder konnten nicht vorbereitet werden.');
    }
  };

  const getQueuedDocumentFiles = (docId: string): UploadedDocumentFile[] => {
    return queueItems
      .filter((item: any) => item.type === 'document_upload' && item.data?.documentId === docId && item.data?.phaseId === phaseId)
      .flatMap((item: any) => {
        // Ab #1216 liegen Bilddaten wartender Items im separaten Blob-Store (queueService
        // extractQueueBlobs) - `item.data` trägt dann nur noch `files[].blobRef`/
        // `imageDataRef` statt `dataUrl`/`imageData`. Beide Formen werden hier abgedeckt,
        // damit sowohl neu eingereihte als auch noch im Altformat wartende Items eine
        // Vorschau bekommen (Code-Review-Finding #1216).
        const files = Array.isArray(item.data?.files)
          ? item.data.files
          : item.data?.imageData
            ? [{ id: `${item.id}-legacy`, name: 'Ausstehendes Bild', type: 'image/jpeg', dataUrl: item.data.imageData }]
            : item.data?.imageDataRef
              ? [{ id: `${item.id}-legacy`, name: 'Ausstehendes Bild', type: 'image/jpeg', blobRef: item.data.imageDataRef }]
              : [];

        return files.map((file: any, index: number) => ({
          id: `queued-${item.id}-${file.id || index}`,
          name: file.name || 'Ausstehendes Bild',
          mimeType: file.type || file.mimeType || 'image/jpeg',
          source: 'local' as const,
          previewData: file.dataUrl,
          queuedBlobRef: file.blobRef,
        }));
      });
  };

  const openDocumentGallery = (document: DocumentType, files: UploadedDocumentFile[]) => {
    setGalleryDocument(document);
    setGalleryFiles(files);
    setGalleryIndex(0);
    setGalleryPreviewData("");
  };

  const closeDocumentGallery = () => {
    setGalleryDocument(null);
    setGalleryFiles([]);
    setGalleryIndex(0);
    setGalleryPreviewData("");
    setGalleryLoading(false);
  };

  const getFilePreviewKey = (file: UploadedDocumentFile) => file.fileId || file.url || file.id;

  const fetchDriveFilePreview = async (driveFileId: string) => {
    const d = await getDriveFile(driveFileId);
    if (d?.base64Content) {
      return `data:${d.mimeType};base64,${d.base64Content}`;
    }
    return "";
  };

  const resolveFilePreview = async (file: UploadedDocumentFile | undefined) => {
    if (!file) {
      return "";
    }

    if (file.previewData) {
      return file.previewData;
    }

    // Noch in der Offline-Queue wartendes Foto/Dokument im neuen Blob-Referenz-Format
    // (#1216, siehe getQueuedDocumentFiles) - peekQueueBlob wirft NICHT, wenn der Blob fehlt
    // (z.B. inzwischen erfolgreich hochgeladen und aufgeräumt), dann fällt diese Funktion auf
    // die übliche Auflösung (Drive/SignedUrl) weiter unten zurück.
    if (file.queuedBlobRef) {
      const queuedPreview = await peekQueueBlob(file.queuedBlobRef);
      if (queuedPreview) {
        return queuedPreview;
      }
    }

    if (file.thumbnailUrl) {
      return file.thumbnailUrl;
    }

    try {
      const driveFileId = file.url ? getDriveFileIdFromUrl(file.url) : file.source === 'drive' ? file.fileId : null;
      if (driveFileId) {
        return await fetchDriveFilePreview(driveFileId);
      }

      if (file.url) {
        return file.url;
      }

      if (file.fileId) {
        const url = await getSignedUrl(file.fileId);
        return url || "";
      }
    } catch (e) {
      console.error('[Preview] Bild konnte nicht geladen werden', e);
    }

    return "";
  };

  // Anders als resolveFilePreview (nur für die kleinen Thumbnail-Kacheln, wo Googles
  // niedrigaufgelöster thumbnailLink völlig ausreicht) lädt diese Variante für die GROSSE
  // Galerie-Vorschau bevorzugt die tatsächliche Bilddatei, statt vorschnell auf thumbnailUrl
  // zurückzufallen — sonst wird dort nur ein winziges, verpixeltes Thumbnail groß angezeigt
  // (Nutzer-Rückmeldung).
  const resolveFullFilePreview = async (file: UploadedDocumentFile | undefined) => {
    if (!file) {
      return "";
    }

    if (file.previewData) {
      return file.previewData;
    }

    try {
      const driveFileId = file.url ? getDriveFileIdFromUrl(file.url) : file.source === 'drive' ? file.fileId : null;
      if (driveFileId) {
        const fullPreview = await fetchDriveFilePreview(driveFileId);
        if (fullPreview) {
          return fullPreview;
        }
      }
    } catch (e) {
      console.error('[Preview] Bild konnte nicht geladen werden', e);
    }

    // Fallback auf die übliche Auflösung (Thumbnail/Signed-URL), falls die volle Datei nicht
    // geladen werden konnte (z.B. kein driveFileId ableitbar).
    return resolveFilePreview(file);
  };

  const loadGalleryPreview = async (file: UploadedDocumentFile | undefined) => {
    setGalleryLoading(true);
    const preview = await resolveFullFilePreview(file);
    setGalleryPreviewData(preview);
    setGalleryLoading(false);
  };

  useEffect(() => {
    if (!galleryDocument) return;
    loadGalleryPreview(galleryFiles[galleryIndex]);
  }, [galleryDocument, galleryFiles, galleryIndex]);

  useEffect(() => {
    let cancelled = false;
    const candidates = documents.flatMap((doc) => {
      if (!isDurchfuehrungMultiUploadDocument(doc)) return [];
      return [
        ...getDocumentFiles(phaseId, doc._id),
        ...getQueuedDocumentFiles(doc._id),
      ].slice(0, 4);
    });

    const uniqueCandidates = candidates.filter((file, index, allFiles) => {
      const key = getFilePreviewKey(file);
      return key && !file.previewData && !thumbnailCache[key] && allFiles.findIndex((entry) => getFilePreviewKey(entry) === key) === index;
    });

    if (uniqueCandidates.length === 0) return;

    uniqueCandidates.forEach((file) => {
      const key = getFilePreviewKey(file);
      void resolveFilePreview(file).then((preview) => {
        if (!preview || cancelled) return;
        setThumbnailCache((previous) => previous[key] ? previous : { ...previous, [key]: preview });
      });
    });

    return () => {
      cancelled = true;
    };
  }, [documents, phaseId, queueItems]);

  const handleDocumentSelect = async (document: DocumentType) => {
    if (document._id === 'seriennummer-ausseneinheit') {
      const serialNumber = getDocumentData(phaseId, 'seriennummer-ausseneinheit-text')
        || ((document as any).fileId as string | undefined);
      if (serialNumber) {
        setSerialPreviewTitle(document.name);
        setSerialPreviewText(serialNumber);
      } else if (onSelectDocument) {
        onSelectDocument(document);
      }
      return;
    }

    if (document._id === 'seriennummer-registrieren') {
      if (document.status === 'completed') {
        // Code-Review-Finding (PR #431/#432): Consent-Zeitstempel liegt jetzt unter einem
        // eigenen Schlüssel (siehe PhaseScreen.performSeriennummerRegistrierenSubmit /
        // queueService.processVaillantSerialRegister), damit 'seriennummer-registrieren-text'
        // ausschließlich die bestätigte Seriennummer enthält. Fallback auf den alten Schlüssel
        // für bereits vor diesem Fix abgeschlossene Registrierungen auf Bestandsgeräten.
        const raw = getDocumentData(phaseId, 'seriennummer-registrieren-consent-text')
          || getDocumentData(phaseId, 'seriennummer-registrieren-text')
          || ((document as any).fileId as string | undefined);
        const display = raw ? formatConsentTimestamp(raw) : null;
        if (display) {
          setSerialPreviewTitle('Registrierung übermittelt');
          setSerialPreviewText(display);
        }
      } else if (onSelectDocument) {
        onSelectDocument(document);
      }
      return;
    }

    if (isDurchfuehrungMultiUploadDocument(document)) {
      const uploadedPairs = pairUploadedFilesWithMarkedVersions(getDocumentFiles(phaseId, document._id));
      const files = [
        ...uploadedPairs.map(toDisplayFile),
        ...getQueuedDocumentFiles(document._id),
      ];
      if (files.length > 0) {
        openDocumentGallery(document, files);
        return;
      }
      return;
    }

    // Wenn wir schon eine URL im Store haben (durch den Helper oben), nimm die!
    const localData = getDocumentData(phaseId, document._id);
    if (localData && localData.startsWith('https://drive.google.com/file/d/')) {
        await openDrivePreview(localData, document);
        return;
    }
    if (localData && localData.startsWith('http')) {
        setPreviewDocument(document);
        setPreviewData(localData);
        setPreviewModalOpen(true);
        return;
    }

    const hubspotDoc = document as any;
    if (hubspotDoc.fileId && hubspotDoc.status === "completed") {
      if (hubspotDoc.fileId.startsWith('https://drive.google.com/')) {
        await openDrivePreview(hubspotDoc.fileId, document);
      } else {
        handleOpenHubSpotDocument(hubspotDoc.fileId, document);
      }
      return;
    }
    if (document.status === "completed") {
      const storedFileId = getDocumentFileId(phaseId, document._id);
      if (storedFileId) {
        handleOpenHubSpotDocument(storedFileId, document);
      } else {
        handleOpenLocalDocumentPreview(document);
      }
      return;
    }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const handleOpenLocalDocumentPreview = async (document: DocumentType) => {
    let data = getDocumentData(phaseId, document._id);
    if (!data) {
        const qItem = queueItems.find((i: any) => i.data?.documentId === document._id);
        if (qItem?.data?.imageData) {
          data = qItem.data.imageData;
        } else if (qItem?.data?.imageDataRef) {
          // Neues Blob-Referenz-Format (#1216, siehe extractQueueBlobs) - peekQueueBlob wirft
          // nicht, wenn der Blob fehlt, `data` bleibt dann einfach leer (Code-Review-Finding
          // #1216: sonst ließe sich ein als "erledigt" markiertes Einzelbild nicht mehr öffnen).
          data = (await peekQueueBlob(qItem.data.imageDataRef)) ?? null;
        }
    }
    if(!data) {
      if (onSelectDocument) onSelectDocument(document);
      return;
    }
    setPreviewDocument(document);
    setPreviewData(data);
    setPreviewModalOpen(true);
  };

  const handleOpenHubSpotDocument = async (fileId: string, document: DocumentType) => {
    try {
      const url = await getSignedUrl(fileId);
      if(!url) throw new Error("No URL");
      setPreviewDocument(document);
      setPreviewData(url);
      setPreviewModalOpen(true);
    } catch(e) { console.error(e); alert("Fehler beim Laden."); }
  };

  const getDriveFileId = (docId: string): string | null => {
    const url = getDocumentData(phaseId, docId);
    if (!url?.startsWith('https://drive.google.com/file/d/')) return null;
    return url.split('/d/')[1]?.split('/')[0] ?? null;
  };

  const handleDeleteDocument = async (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    if(window.confirm("Bist du sicher, dass du dieses Dokument löschen willst?")) {
      const driveFileId = getDriveFileId(docId);
      if (driveFileId) {
        try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Delete] Drive-Fehler:', err); }
      }
      await deleteDocument(phaseId, docId);
      setActionMenuOpen(null);
    }
  };

  const handleReplaceDocument = async (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setActionMenuOpen(null);
    if (isDurchfuehrungMultiUploadDocument(document)) {
      return;
    }
    // const driveFileId = getDriveFileId(document._id);
    // if (driveFileId) {
    //   try { await deleteDriveFile(driveFileId); } catch (err) { console.error('[Replace] Drive-Fehler:', err); }
    // }
    if (hasFormularTemplate(document)) {
      handleOpenFormular(undefined, document);
    } else {
      if (onSelectDocument) onSelectDocument(document);
    }
  };

  const toggleActionMenu = (e: React.MouseEvent, docId: string) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === docId ? null : docId);
  };

  const handleOpenFormular = (e: React.MouseEvent | undefined, document: DocumentType) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    setSelectedFormular(document);
    setFormularModalOpen(true);
  };

  // === Formular Logic ===
  const hasFormularTemplate = (doc: DocumentType) => {
    const ids = ["inbetriebnahmeprotokoll", "vdz-formular", "hydraulischer-abgleich"];
    return ids.includes(doc._id) || doc.name.toLowerCase().includes("inbetriebnahm") || doc.name.toLowerCase().includes("vdz");
  };

  const getTemplatePath = (docId: string) => {
    if(docId.includes("inbetriebnahm")) return "/Templates/Inbetriebnahmeprotokoll.html";
    return "/Templates/VdZ_Formular.html"; 
  };

  // UPDATE: Webhook Success Handler
  const handleFormularUploadSuccess = async (fileId?: string) => {
    if (!selectedFormular) return;
    
    console.log(`[CHECKLIST] Upload Success. ID: ${fileId}`);
    
    if (fileId) {
        // 1. ID speichern (setzt auch status auf "completed" im Store)
        saveDocumentFileId(phaseId, selectedFormular._id, fileId);
        
        // 2. AWAIT: Helper ruft URL ab und speichert sie
        // Damit wird das "alte" Bild sofort durch das PDF ersetzt
        await fetchAndCacheHubSpotUrl(fileId, selectedFormular._id);
    } else {
        // Fallback: Nur Status setzen wenn keine fileId vorhanden
        updateDocumentStatus(phaseId, selectedFormular._id, "completed");
    }
	  };

  const getUploadedFileKey = (file: UploadedDocumentFile) => file.fileId || file.url || file.id;

  // Ticket #1055: dieselbe Drive-fileId-Ableitung wie resolveFilePreview — nur Drive-Fotos (kein
  // PDF) lassen sich markieren, HubSpot-/externe/lokale Dateien haben keinen Re-Upload-Mechanismus.
  const resolveDriveFileId = (file: UploadedDocumentFile | undefined): string | null => {
    if (!file) return null;
    if (file.url) return getDriveFileIdFromUrl(file.url);
    return file.source === 'drive' ? file.fileId || null : null;
  };

  const isEditableGalleryPhoto = (file: UploadedDocumentFile | undefined): boolean =>
    Boolean(file) && file!.mimeType !== 'application/pdf' && resolveDriveFileId(file) !== null;

  const deleteDriveFileForReference = async (file: UploadedDocumentFile) => {
    const driveFileId = file.url
      ? getDriveFileIdFromUrl(file.url)
      : file.source === 'drive'
        ? file.fileId
        : null;

    if (!driveFileId) return;

    try {
      await deleteDriveFile(driveFileId);
    } catch (err) {
      console.error('[Delete] Drive-Fehler:', err);
    }
  };

  const syncMultiUploadProperty = async (document: DocumentType, files: UploadedDocumentFile[]) => {
    const dealId = phaseId.split('-')[0];
    const propertyName = DOCUMENT_TO_PROPERTY_MAP[document._id];
    if (!dealId || !propertyName) return;

    const propertyValue = files.length > 0 ? uploadedDocumentFilesToPropertyValue(files) : '';
    await updateDealTextProperty(dealId, propertyName, propertyValue);

    // Nutzer-Rückmeldung: Nach einem Löschen erschien das Bild nach F5 wieder. Ursache: PhaseScreen
    // liest beim Reload bevorzugt den PERSISTIERTEN Deal-Cache (useDealsStore) statt neu zu laden,
    // und extractDocumentStatusWithTypes leitet uploadedFiles direkt aus `deal[propertyName]` ab.
    // Ohne dieses Update bleibt der gecachte Deal auf dem alten (Vor-Löschen-)Wert stehen, und
    // mergeUploadedDocumentFiles (rein additiv, kann nichts entfernen) holt die gelöschte Datei
    // beim Reload wieder zurück in documentFiles[key]. Denselben Deal per Objekt-Referenz aus dem
    // Store holen und NUR dieses eine Feld patchen (wie in queueService.ts:
    // processProjektdokumentationStatusUpdate) — kein Bulk-Replace des Deals, das hat schon einmal
    // die Deal-Liste beim Zurück-Navigieren auf 1 Eintrag schrumpfen lassen (s. Memory).
    const existingDeal = useDealsStore.getState().deals.find((deal) => deal.id === dealId);
    if (existingDeal) {
      useDealsStore.getState().upsertDeal({
        ...existingDeal,
        [propertyName]: propertyValue,
      });
    }
  };

  const saveMultiUploadFiles = async (document: DocumentType, files: UploadedDocumentFile[]) => {
    saveDocumentFiles(phaseId, document._id, files);
    saveDocumentData(phaseId, document._id, files.find((file) => file.url)?.url || '');
    await syncMultiUploadProperty(document, files);
  };

  const handleOpenGalleryEdit = async () => {
    const file = galleryFiles[galleryIndex];
    if (!isEditableGalleryPhoto(file)) return;
    const driveFileId = resolveDriveFileId(file);
    if (!driveFileId) return;

    // Ist die angezeigte Datei selbst eine Markierung (Original+Markierung sind gepaart, s.
    // pairUploadedFilesWithMarkedVersions)? Dann zusätzlich das unangetastete Original laden,
    // damit "Löschen" im Editor darauf zurückspringen kann — wie in Projektvorbereitung/VOT
    // (usePhotoAnnotationGallery.openAnnotation).
    const storedFiles = galleryDocument ? getDocumentFiles(phaseId, galleryDocument._id) : [];
    const pair = pairUploadedFilesWithMarkedVersions(storedFiles).find(
      (candidate) => getUploadedFileKey(toDisplayFile(candidate)) === getUploadedFileKey(file)
    );
    const original = pair?.marked ? pair.original : null;
    const originalDriveFileId = original ? resolveDriveFileId(original) : null;

    setEditingGalleryFile(file);
    setEditingGalleryOriginal(original);
    setEditingImageDataUrl(null);
    setEditingOriginalImageDataUrl(undefined);
    setEditingLoading(true);
    try {
      const [content, originalContent] = await Promise.all([
        getDriveFile(driveFileId),
        originalDriveFileId ? getDriveFile(originalDriveFileId) : Promise.resolve(null),
      ]);
      if (content) {
        setEditingImageDataUrl(`data:${content.mimeType};base64,${content.base64Content}`);
      }
      if (originalContent) {
        setEditingOriginalImageDataUrl(`data:${originalContent.mimeType};base64,${originalContent.base64Content}`);
      }
    } finally {
      setEditingLoading(false);
    }
  };

  const handleCloseGalleryEdit = () => {
    setEditingGalleryFile(null);
    setEditingGalleryOriginal(null);
    setEditingImageDataUrl(null);
    setEditingOriginalImageDataUrl(undefined);
  };

  // Löscht+Übernehmen im Editor ohne neue Striche auf dem zurückgesprungenen Original (per
  // "Löschen"-Icon) heißt: die Markierung ist überflüssig geworden — die markierte Datei wird
  // entfernt, das Original bleibt unangetastet erhalten (identisches Verhalten zu
  // usePhotoAnnotationGallery.deleteMarkedAnnotation in Projektvorbereitung/VOT).
  const handleDeleteMarkedGalleryAnnotation = async () => {
    if (!galleryDocument || !editingGalleryFile || !editingGalleryOriginal) return;
    setSavingEdit(true);
    try {
      await deleteDriveFileForReference(editingGalleryFile);

      const markedKey = getUploadedFileKey(editingGalleryFile);
      const storedFiles = getDocumentFiles(phaseId, galleryDocument._id);
      const nextStoredFiles = storedFiles.filter((entry) => getUploadedFileKey(entry) !== markedKey);
      await saveMultiUploadFiles(galleryDocument, nextStoredFiles);

      const nextPairs = pairUploadedFilesWithMarkedVersions(nextStoredFiles);
      const nextGalleryFiles = nextPairs.map(toDisplayFile);
      const nextIndex = nextGalleryFiles.findIndex(
        (entry) => getUploadedFileKey(entry) === getUploadedFileKey(editingGalleryOriginal)
      );
      setGalleryFiles(nextGalleryFiles);
      setGalleryIndex(nextIndex >= 0 ? nextIndex : Math.max(nextGalleryFiles.length - 1, 0));
      handleCloseGalleryEdit();
    } catch (err) {
      console.error('[DocumentChecklist] Markierung löschen fehlgeschlagen', err);
      onError?.(err instanceof Error ? err.message : 'Markierung konnte nicht gelöscht werden');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveGalleryEdit = async (annotatedDataUrl: string, caption: string) => {
    if (!galleryDocument || !editingGalleryFile || !projectId) return;
    setSavingEdit(true);
    try {
      // Original-Referenz bleibt beim erneuten Bearbeiten einer bereits markierten Version
      // erhalten (editingGalleryFile.originalId zeigt dann schon auf das eigentliche Original,
      // nicht auf sich selbst) — sonst würde jede weitere Bearbeitung eine neue, zusätzliche
      // Markierung anlegen statt die bestehende zu ersetzen.
      const originalKey = editingGalleryFile.originalId || getUploadedFileKey(editingGalleryFile);
      // Der lokal gecachte Name (z.B. der Browser-Dateiname beim Upload) kann vom tatsächlichen
      // Drive-Namen abweichen (Drive vergibt teils einen eigenen, generierten Namen) — der
      // "-markiert"-Name muss zum ECHTEN Drive-Namen passen, sonst wirkt die Markierung im Drive-
      // Ordner wie eine unabhängige, unzusammenhängende Datei (Nutzer-Rückmeldung).
      const driveFileId = resolveDriveFileId(editingGalleryFile);
      const driveMetadata = driveFileId ? await getDriveFileMetadataAsync(driveFileId) : null;
      const baseName = driveMetadata?.name || editingGalleryFile.name || originalKey || 'Foto';
      const markedFileName = withMarkedFileNameSuffix(baseName);
      // Multi-Upload-Dokumente (z.B. Baustellendoku, Ticket #1055) landen unter
      // Dokumente/<Phasenordner> (queueService.processDocumentUpload), nicht unter Bilder/* wie
      // Projektvorbereitung/VOT — die Markierung muss in denselben Ordner wie das Original.
      const markedDirectories = ['Dokumente', resolvePhaseFolderName(phaseId)];
      const result = await uploadMarkedPhotoAsync(projectId, markedDirectories, annotatedDataUrl, markedFileName, caption);

      const updatedFile: UploadedDocumentFile = {
        id: result.fileId,
        fileId: result.fileId,
        url: result.webViewLink,
        name: result.fileName ?? markedFileName,
        mimeType: 'image/jpeg',
        source: 'drive',
        thumbnailUrl: result.thumbnailLink,
        description: result.description ?? (caption || undefined),
        originalId: originalKey,
      };

      const storedFiles = getDocumentFiles(phaseId, galleryDocument._id);
      // Nicht per Dateiname matchen (das Original hat hier oft gar keinen Namen, s. Pairing-
      // Kommentar oben) — per originalId findet sich eine bereits vorhandene Markierung für
      // GENAU dieses Original, unabhängig von Dateinamen.
      const existingIndex = storedFiles.findIndex((entry) => entry.originalId === originalKey);
      const nextStoredFiles =
        existingIndex >= 0
          ? storedFiles.map((entry, index) => (index === existingIndex ? updatedFile : entry))
          : [...storedFiles, updatedFile];

      await saveMultiUploadFiles(galleryDocument, nextStoredFiles);

      // Nutzer-Rückmeldung: ohne dies zeigte die Kachel/Thumbnail-Leiste bis zum nächsten F5
      // weiterhin das Original — Googles thumbnailLink für eine gerade erst hochgeladene Datei
      // ist oft noch nicht (rechtzeitig) verfügbar. annotatedDataUrl liegt bereits lokal vor
      // (identisch zu usePhotoAnnotationGallery.onSaved in Projektvorbereitung/VOT) und kann
      // sofort als Thumbnail übernommen werden, statt auf Drive zu warten.
      setThumbnailCache((previous) => ({ ...previous, [result.fileId]: annotatedDataUrl }));

      // Original+Markierung werden hier zu einer Kachel zusammengeführt (s. Nutzer-Rückmeldung) —
      // die Galerie zeigt deshalb die gepaarte Liste, nicht die rohe Speicherliste.
      const nextPairs = pairUploadedFilesWithMarkedVersions(nextStoredFiles);
      const nextGalleryFiles = nextPairs.map(toDisplayFile);
      const nextIndex = nextGalleryFiles.findIndex((entry) => entry.originalId === originalKey || uploadedFileKey(entry) === originalKey);
      setGalleryFiles(nextGalleryFiles);
      setGalleryIndex(nextIndex >= 0 ? nextIndex : nextGalleryFiles.length - 1);
      handleCloseGalleryEdit();
    } catch (err) {
      console.error('[DocumentChecklist] Foto markieren fehlgeschlagen', err);
      onError?.(err instanceof Error ? err.message : 'Foto konnte nicht markiert werden');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteAllImages = async (e: React.MouseEvent, document: DocumentType) => {
    e.stopPropagation();
    setActionMenuOpen(null);
    if (!window.confirm('Bist du sicher, dass du alle Bilder löschen willst?')) {
      return;
    }

    const files = getDocumentFiles(phaseId, document._id);
    // Code-Review-Finding (Re-Review, zu #1199): erst ALLE wartenden Queue-Items gesammelt
    // awaiten und bei einem Fehlschlag die fachliche Löschung ganz abbrechen, statt
    // fire-and-forget zu laufen. Sonst bliebe ein noch nicht synchronisiertes
    // document_upload-Item trotz gelöschtem Drive-File/Dokumentstatus in der Queue liegen und
    // würde beim nächsten Sync erneut hochgeladen - das vom Nutzer gelöschte Dokument
    // erschiene dann unerwartet wieder.
    const queuedUploadItems = queueItems.filter(
      (item: any) => item.type === 'document_upload' && item.data?.documentId === document._id && item.data?.phaseId === phaseId
    );
    try {
      await Promise.all(queuedUploadItems.map((item: any) => removeFromQueue(item.id)));
    } catch (error) {
      console.error('Wartende Uploads konnten nicht entfernt werden, Löschung abgebrochen:', error);
      onError?.('Bilder konnten nicht gelöscht werden — bitte erneut versuchen.');
      return;
    }
    await Promise.all(files.map(deleteDriveFileForReference));
    await deleteDocument(phaseId, document._id);
    if (galleryDocument?._id === document._id) {
      closeDocumentGallery();
    }
  };

  const handleDeleteCurrentGalleryImage = async () => {
    if (!galleryDocument) return;
    const file = galleryFiles[galleryIndex];
    if (!file) return;

    if (!window.confirm('Bist du sicher, dass du dieses Bild löschen willst?')) {
      return;
    }

    const fileKey = getUploadedFileKey(file);
    const storedFiles = getDocumentFiles(phaseId, galleryDocument._id);
    // `file` ist die angezeigte (gepaarte) Kachel — bei einer markierten Version muss auch das
    // zugrundeliegende, unangetastete Original mitgelöscht werden, sonst taucht es beim nächsten
    // Öffnen wieder als eigene Kachel auf (Nutzer-Rückmeldung).
    const pair = pairUploadedFilesWithMarkedVersions(storedFiles).find(
      (candidate) => getUploadedFileKey(toDisplayFile(candidate)) === fileKey
    );
    const filesToDelete = pair ? [pair.original, ...(pair.marked ? [pair.marked] : [])] : [file];
    const keysToDelete = new Set(filesToDelete.map(getUploadedFileKey));

    const nextGalleryFiles = galleryFiles.filter((entry) => !keysToDelete.has(getUploadedFileKey(entry)));
    const nextStoredFiles = storedFiles.filter((entry) => !keysToDelete.has(getUploadedFileKey(entry)));

    await Promise.all(filesToDelete.map((entry) => deleteDriveFileForReference(entry)));
    await saveMultiUploadFiles(galleryDocument, nextStoredFiles);

    if (nextGalleryFiles.length === 0) {
      closeDocumentGallery();
      return;
    }

    setGalleryFiles(nextGalleryFiles);
    setGalleryIndex((current) => Math.min(current, nextGalleryFiles.length - 1));
  };

  // === RENDER ===
  const getStatusBadge = (req: boolean) => req ? 
    <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Pflicht</span> : 
    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Optional</span>;

  return (
    // Ticket #1126: kein eigenes Bottom-Padding mehr — jede Dokument-Karte bringt bereits ihr
    // eigenes mb-4 mit, und die Bottom-Nav-Clearance übernimmt bereits das <main> in PhaseScreen
    // (pb-[calc(...+6rem)]). Ein zusätzliches pb-24/pb-4 hier addierte sich bei mehreren
    // DocumentChecklist-Instanzen auf derselben Seite (Außenfühler jetzt separat gerendert) zu
    // sichtbar größeren Lücken genau an den Instanz-Grenzen.
    <div data-testid="document-checklist">
      {loading && <div className="p-4 text-center">Lade...</div>}
      {error && <div className="p-4 text-red-500">{error}</div>}

      {!loading && !error && visibleDocuments.map((doc) => {
        const canOpenExistingDocument = doc.status === 'completed';
        const isMultiUploadDocument = isDurchfuehrungMultiUploadDocument(doc);
        const uploadedFiles = isMultiUploadDocument ? getDocumentFiles(phaseId, doc._id) : [];
        const queuedFiles = isMultiUploadDocument ? getQueuedDocumentFiles(doc._id) : [];
        const uploadedPairs = pairUploadedFilesWithMarkedVersions(uploadedFiles);
        const displayFiles = [...uploadedPairs.map(toDisplayFile), ...queuedFiles];
	        const uploadedCount = displayFiles.length;
	        // `queuedFiles` sind lokal wartende, noch NICHT tatsächlich hochgeladene Fotos (siehe
	        // getQueuedDocumentFiles) - eine reine "X Bilder hochgeladen"-Zählung über alle
	        // displayFiles hinweg hätte diese fälschlich als bereits übertragen ausgewiesen (ADO
	        // #1231, Kriterium 2). Getrennt ausweisen, solange noch etwas in der Queue wartet.
	        const uploadLabel = queuedFiles.length > 0
	          ? `${uploadedPairs.length} ${uploadedPairs.length === 1 ? 'Bild' : 'Bilder'} hochgeladen · ${queuedFiles.length} in Warteschlange`
	          : uploadedCount === 1 ? '1 Bild hochgeladen' : `${uploadedCount} Bilder hochgeladen`;
	        const visiblePreviewFiles = displayFiles.slice(0, 4);
	        const canPreviewDocument = isMultiUploadDocument
	          ? uploadedCount > 0
	          : Boolean((doc as any).fileId || getDocumentFileId(phaseId, doc._id) || getDocumentData(phaseId, doc._id));

	        return (
	        <div key={doc._id}
             data-testid="document-item"
             data-testid-card="document-card"
	             onClick={() => (!disabled || canOpenExistingDocument) && handleDocumentSelect(doc)}
	             className={`w-full bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4 transition-all ${doc.status === 'completed' ? 'border-l-4 border-green-500' : ''}`}>
	          {isMultiUploadDocument && (
            <input
              id={getDirectPhotoInputId(doc)}
              data-testid="direct-photo-input"
              data-document-id={doc._id}
              type="file"
              accept="image/*"
              multiple
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleDirectPhotoInputChange(e, doc)}
              className="sr-only"
            />
	          )}

          <div className="flex justify-between mb-2">
            <div>
                <h3 className="font-bold text-gray-900 dark:text-white">{doc.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{doc.description}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
                {getStatusBadge(doc.required)}
            </div>
          </div>

          {isMultiUploadDocument && uploadedCount > 0 && (
            <div className="mt-3">
              <div className="mb-2 text-sm font-medium text-green-700 dark:text-green-400">
                {uploadLabel}
              </div>
              <div className="flex gap-2 overflow-hidden" aria-label={`${doc.name} Upload-Vorschau`}>
	                {visiblePreviewFiles.map((file, index) => (
	                  (() => {
	                    const previewSrc = file.previewData || thumbnailCache[getFilePreviewKey(file)] || file.thumbnailUrl;
	                    return (
	                  <button
	                    key={file.id}
	                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDocumentGallery(doc, displayFiles);
                      setGalleryIndex(index);
                    }}
		                    className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300"
	                    aria-label={`${doc.name} Bild ${index + 1} anzeigen`}
	                  >
	                    {previewSrc ? (
	                      <img src={previewSrc} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
	                    ) : (
	                      <span className="flex h-full w-full items-center justify-center">Lade...</span>
	                    )}
	                    {file.description && <CaptionIndicator caption={file.description} />}
	                  </button>
	                    );
	                  })()
	                ))}
                {displayFiles.length > visiblePreviewFiles.length && (
	                  <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-200">
                    +{displayFiles.length - visiblePreviewFiles.length}
                  </div>
                )}
              </div>
            </div>
          )}

	          <div className="flex justify-between items-center mt-3">
		            {doc.status === 'completed' ? (
		                <button
		                  type="button"
		                  onClick={(e) => {
		                    e.stopPropagation();
		                    if (canPreviewDocument) {
		                      handleDocumentSelect(doc);
		                    }
		                  }}
		                  disabled={!canPreviewDocument}
		                  className="inline-flex items-center rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 transition-colors hover:border-green-300 hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300 dark:hover:bg-green-900/30 dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
		                >
		                  <span className="mr-1">✓</span>
		                  {canPreviewDocument ? "Anzeigen" : "Wird verarbeitet..."}
		                </button>
		            ) : (
                isMultiUploadDocument ? (
                  <label
                    htmlFor={disabled ? undefined : getDirectPhotoInputId(doc)}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-disabled={disabled}
                    onClick={(e) => e.stopPropagation()}
                    className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      disabled
                        ? 'cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    Bilder hinzufügen
                  </label>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!disabled) {
                        handleDocumentSelect(doc);
                      }
                    }}
                    disabled={disabled}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
                  >
                    Dokument hinzufügen
                  </button>
                )
	            )}

            {doc.status === 'completed' && (
                <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => toggleActionMenu(e, doc._id)}
                      className="p-2 text-gray-500 dark:text-gray-400"
                      aria-label={`${doc.name} Aktionen`}
                      aria-expanded={actionMenuOpen === doc._id}
                    >
                      •••
                    </button>
                    {actionMenuOpen === doc._id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-700 shadow-xl border border-gray-200 dark:border-slate-600 rounded z-50">
                            <button onClick={(e) => { e.stopPropagation(); handleDocumentSelect(doc); }} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Anzeigen</button>
                            {isMultiUploadDocument ? (
                              <>
                                <label
                                  htmlFor={getDirectPhotoInputId(doc)}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => e.stopPropagation()}
                                  className="block w-full cursor-pointer px-4 py-2 text-left text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-slate-600"
                                >
                                  Bilder hinzufügen
                                </label>
                                <button onClick={(e) => handleDeleteAllImages(e, doc)} className="block w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Alle Bilder löschen</button>
                              </>
                            ) : (
                              <>
                                <button onClick={(e) => handleReplaceDocument(e, doc)} className="block w-full text-left px-4 py-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-slate-600">Ersetzen</button>
                                <button onClick={(e) => handleDeleteDocument(e, doc._id)} className="block w-full text-left px-4 py-2 text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-600">Löschen</button>
                              </>
                            )}
                        </div>
                    )}
                </div>
            )}
          </div>
        </div>
        );
      })}

      {formularModalOpen && selectedFormular && (
        <FormularModal
          isOpen={formularModalOpen}
          onClose={() => { setFormularModalOpen(false); setSelectedFormular(null); }}
          templatePath={getTemplatePath(selectedFormular._id)}
          documentTitle={selectedFormular.name}
          dealId={phaseId.split('-')[0]}
          phaseId={phaseId}
          documentId={selectedFormular._id}
          onUploadComplete={handleFormularUploadSuccess}
          dealContact={dealContact}
        />
      )}

      {serialPreviewText !== null && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4" onClick={() => setSerialPreviewText(null)}>
          <div className="w-full sm:max-w-sm bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl px-6 py-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{serialPreviewTitle}</h2>
              <button onClick={() => setSerialPreviewText(null)} className="text-gray-500 hover:text-gray-800 dark:hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p
              className="font-mono font-bold text-gray-900 dark:text-white text-center py-4 bg-gray-50 dark:bg-gray-700 rounded-xl whitespace-nowrap overflow-hidden"
              style={{ fontSize: `clamp(0.875rem, ${Math.min(2, 35 / Math.max(serialPreviewText.length, 1))}rem, 2rem)` }}
            >
              {serialPreviewText}
            </p>
          </div>
        </div>
      )}

      {previewModalOpen && previewDocument && (
        <DocumentPreviewModal
          isOpen={previewModalOpen}
          onClose={() => { setPreviewModalOpen(false); setPreviewDocument(null); }}
          documentName={previewDocument.name}
          documentType={previewData.startsWith("data:") ? "image" : "pdf"}
          previewData={previewData}
          onReplace={() => handleReplaceDocument(undefined, previewDocument)}
        />
      )}

      {galleryDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4 pb-20" onClick={closeDocumentGallery}>
          <div className="relative flex h-full max-h-[calc(90vh-60px)] w-full max-w-7xl flex-col rounded-lg bg-white shadow-2xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold text-gray-900 dark:text-white">{galleryDocument.name}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Bild {galleryIndex + 1} von {galleryFiles.length}
                </p>
              </div>
              <button
                onClick={closeDocumentGallery}
                className="flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                aria-label="Schließen"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col bg-gray-50 p-3 dark:bg-gray-900">
              <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
	                {galleryFiles.map((file, index) => (
	                  (() => {
	                    const previewSrc = file.previewData || thumbnailCache[getFilePreviewKey(file)] || file.thumbnailUrl;
	                    return (
	                  <button
	                    key={file.id}
	                    type="button"
                    onClick={() => setGalleryIndex(index)}
	                    className={`relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border text-xs ${
                      index === galleryIndex
                        ? 'border-primary-600 ring-2 ring-primary-200'
                        : 'border-gray-200 dark:border-gray-700'
                    } bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`}
	                    aria-label={`Bild ${index + 1} anzeigen`}
	                  >
	                    {previewSrc ? (
	                      <img src={previewSrc} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
	                    ) : (
	                      <span className="flex h-full w-full items-center justify-center">Lade...</span>
	                    )}
	                    {file.description && <CaptionIndicator caption={file.description} />}
	                  </button>
	                    );
	                  })()
	                ))}
              </div>

              <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg bg-black">
                {galleryLoading ? (
                  <div className="text-sm text-white">Lade Bild...</div>
                ) : galleryPreviewData ? (
                  <img
                    src={galleryPreviewData}
                    alt={`${galleryDocument.name} ${galleryIndex + 1}`}
                    className="max-h-full max-w-full object-contain"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="text-sm text-white">Vorschau nicht verfügbar</div>
                )}
              </div>

              {galleryFiles[galleryIndex]?.description && (
                <details className="mt-3 rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-700">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">
                    Beschriftung
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
                    {galleryFiles[galleryIndex].description}
                  </p>
                </details>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setGalleryIndex((current) => Math.max(current - 1, 0))}
                disabled={galleryIndex === 0}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              >
                Zurück
              </button>
              <div className="flex flex-1 justify-center gap-2">
                <label
                  htmlFor={disabled ? undefined : getDirectPhotoInputId(galleryDocument)}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={disabled}
                  onClick={(e) => e.stopPropagation()}
                  className={`rounded-lg px-4 py-2 transition-colors ${
                    disabled
                      ? 'cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      : 'cursor-pointer bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Bilder hinzufügen
                </label>
                {effectiveIsOpsManager && projectId && isEditableGalleryPhoto(galleryFiles[galleryIndex]) && (
                  <button
                    type="button"
                    onClick={handleOpenGalleryEdit}
                    className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-primary-700 transition-colors hover:bg-primary-100 dark:border-primary-900/60 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/30"
                  >
                    Bearbeiten
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDeleteCurrentGalleryImage}
                  className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-300 dark:hover:bg-red-900/30"
                >
                  Bild löschen
                </button>
              </div>
              <button
                type="button"
                onClick={() => setGalleryIndex((current) => Math.min(current + 1, galleryFiles.length - 1))}
                disabled={galleryIndex >= galleryFiles.length - 1}
                className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-300"
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}

      {editingGalleryFile && (
        <div className="fixed inset-0 z-[60] flex flex-col sm:items-center sm:justify-center sm:bg-black sm:bg-opacity-75 sm:p-4 sm:pb-20">
          <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[calc(90vh-60px)] sm:rounded-lg sm:shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                Foto markieren: {editingGalleryFile.name || 'Foto'}
              </h2>
              <button
                onClick={handleCloseGalleryEdit}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0 ml-2"
                aria-label="Schließen"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
              {editingLoading ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">Foto wird geladen</p>
                </div>
              ) : !editingImageDataUrl ? (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
                  Foto konnte nicht geladen werden
                </div>
              ) : (
                <PhotoAnnotationEditor
                  imageDataUrl={editingImageDataUrl}
                  originalImageDataUrl={editingOriginalImageDataUrl}
                  initialCaption={editingGalleryFile.description}
                  onSave={handleSaveGalleryEdit}
                  onDelete={editingGalleryOriginal ? handleDeleteMarkedGalleryAnnotation : undefined}
                  onCancel={handleCloseGalleryEdit}
                  saving={savingEdit}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentChecklist;
